// ============================================================================
// Gmail — attachment byte fetch.
//
// `GET /gmail/v1/users/me/messages/{messageId}/attachments/{attachmentId}`
// returns `{ size, data }` where `data` is base64url-encoded. We decode to
// an ArrayBuffer so callers can stream directly to R2 or return from the
// capability as `ArrayBuffer` per the email capability contract.
// ============================================================================

import { GMAIL_API_BASE } from './oauth'
import { base64UrlDecodeToBytes } from './mime'
import {
  classifyGmailError,
  GmailError,
  parseRetryAfterMs,
  sanitizeGmailErrorMessage,
  type GmailAttachmentPayload,
  type GoogleErrorEnvelope,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface GetGmailAttachmentInput {
  accessToken: string
  messageId: string
  attachmentId: string
  fetchImpl?: typeof fetch
  apiBase?: string
}

// ============================================================================
// getGmailAttachment
// ============================================================================

export async function getGmailAttachment(
  args: GetGmailAttachmentInput,
): Promise<ArrayBuffer> {
  assertAccessToken(args.accessToken)
  if (!args.messageId) {
    throw new GmailError('INVALID_INPUT', 'messageId is required')
  }
  if (!args.attachmentId) {
    throw new GmailError('INVALID_INPUT', 'attachmentId is required')
  }
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE

  const url = `${apiBase}/gmail/v1/users/me/messages/${encodeURIComponent(args.messageId)}/attachments/${encodeURIComponent(args.attachmentId)}`
  const resp = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp)
  }

  const body = (await resp.json()) as GmailAttachmentPayload
  if (!body || typeof body.data !== 'string' || body.data.length === 0) {
    throw new GmailError(
      'PROVIDER_ERROR',
      'Gmail returned attachment without `data` field',
    )
  }
  const bytes = base64UrlDecodeToBytes(body.data)
  // Convert to a standalone ArrayBuffer (not a Uint8Array-over-shared-buffer)
  // so callers can safely transfer / keep references without worrying about
  // the Uint8Array's backing store being reused.
  const out = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(out).set(bytes)
  return out
}

// ============================================================================
// Helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<GmailError> {
  let envelope: GoogleErrorEnvelope | null = null
  let text = ''
  try {
    text = await response.text()
    envelope = text ? (JSON.parse(text) as GoogleErrorEnvelope) : null
  } catch {
    envelope = null
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  if (!envelope && text) {
    return new GmailError(
      response.status === 401 ? 'AUTH_FAILED' : 'PROVIDER_ERROR',
      sanitizeGmailErrorMessage(text).slice(0, 200),
      { httpStatus: response.status, retryAfterMs },
    )
  }
  return classifyGmailError(
    response.status,
    envelope?.error?.errors?.[0]?.reason,
    envelope,
    retryAfterMs,
  )
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GmailError('INVALID_INPUT', 'accessToken is required')
  }
}
